import { useState } from 'react';
import PageSEO from '@/components/feature/PageSEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { testimonials } from '@/mocks/testimonials';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于17c一起',
  description: '17c一起 - 相聚共享，连接你我他。了解我们的品牌故事、使命愿景和团队文化。',
  about: {
    '@type': 'Organization',
    name: '17c一起',
    description: '汇聚志同道合伙伴的互动社交平台。',
    url: 'https://17cyiqi.com',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '17c一起是什么？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '17c一起是一个汇聚志同道合伙伴的互动社交平台。我们致力于连接有共同兴趣爱好的人，让大家可以一起行动、一起分享、一起成长。',
      },
    },
    {
      '@type': 'Question',
      name: '如何加入17c一起？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '你只需要在我们的平台上注册一个账号，选择你感兴趣的领域，就可以开始浏览和参加各种活动了。注册完全免费！',
      },
    },
  ],
};

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('company_alt') as string || '').trim();
    if (honeypot) {
      setFormState('success');
      return;
    }

    setFormState('submitting');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'company_alt' && value && typeof value === 'string') {
          params.append(key, value);
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9phkmhn2trhb35gvfkg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || '';
      const isSpam = serverMsg.toLowerCase().includes('spam') || responseText.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setFormState('success');
        form.reset();
      } else {
        setFormState('error');
        setFormError(serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setFormState('error');
      setFormError('网络错误，请检查网络后重试。');
    }
  };

  return (
    <>
      <PageSEO
        title="关于我们"
        description="了解17c一起的品牌故事和使命愿景。17c一起是汇聚志同道合伙伴的互动平台，随时随地与朋友、同好一起行动、一起分享、一起成长。"
        keywords="17c一起, 17c一起平台, 关于17c一起, 共享互动社区"
        canonicalUrl="https://17cyiqi.com/about"
      />
      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <Navbar />
      <main className="min-h-screen pt-16 bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-5xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Brand Story */}
          <section className="py-12 md:py-16">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-950 mb-4">
                关于 17c一起
              </h1>
              <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
                相聚共享，连接你我他
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-4">
                  我们的故事
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  17c一起诞生于一群热爱生活的年轻人的共同愿景——在这个数字化高速发展的时代，人与人之间的真实连接反而变得越来越稀缺。我们希望打造一个平台，让有共同兴趣爱好的人能够突破屏幕的限制，面对面地交流、分享和成长。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  我们的名字"17c一起"蕴含着深刻的意义。"17"谐音"一起"，"c"代表"connect"（连接），我们希望每个人都能在这里找到志同道合的伙伴，一起探索这个精彩的世界。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  从最初的几个人小范围聚会，到如今覆盖八大兴趣领域、拥有数万名活跃用户的社区平台，17c一起始终坚守着"连接你我他"的初心。
                </p>
              </div>
              <div className="rounded-lg overflow-hidden h-64 md:h-80">
                <img
                  src="https://readdy.ai/api/search-image?query=Diverse%20group%20of%20Chinese%20young%20friends%20laughing%20and%20talking%20together%20in%20bright%20modern%20space%2C%20warm%20natural%20light%2C%20genuine%20connection%2C%20community%20atmosphere%2C%20clean%20editorial%20lifestyle%20photography%2C%20soft%20warm%20tones&width=800&height=500&seq=about-story&orientation=landscape"
                  alt="17c一起社区故事"
                  title="17c一起品牌故事 - 相聚共享"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </section>

          {/* Mission & Values */}
          <section className="py-12 md:py-16 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 text-center mb-10">
                我们的使命与价值观
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6">
                <div className="bg-background-50 rounded-lg p-5 text-center border border-background-200/70">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-primary-100">
                    <i className="ri-group-line text-xl text-primary-500"></i>
                  </div>
                  <h3 className="text-sm font-label font-medium text-foreground-900 mb-2">连接</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    打破社交壁垒，让志同道合的人能够轻松相遇和连接，建立真实而有意义的人际关系。
                  </p>
                </div>
                <div className="bg-background-50 rounded-lg p-5 text-center border border-background-200/70">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-accent-100">
                    <i className="ri-heart-line text-xl text-accent-500"></i>
                  </div>
                  <h3 className="text-sm font-label font-medium text-foreground-900 mb-2">分享</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    鼓励知识和经验的分享，让每个人都能在社区中获得成长，同时用自己的专长帮助他人。
                  </p>
                </div>
                <div className="bg-background-50 rounded-lg p-5 text-center border border-background-200/70">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-secondary-100">
                    <i className="ri-rocket-line text-xl text-secondary-500"></i>
                  </div>
                  <h3 className="text-sm font-label font-medium text-foreground-900 mb-2">成长</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    在共同的兴趣和行动中不断进步，让17c一起成为每个人成长路上的温暖陪伴。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* User Testimonials */}
          <section className="py-12 md:py-16">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 text-center mb-3">
                用户口碑
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-10">
                来自社区成员的真实声音
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {testimonials.map((t) => (
                  <div
                    key={t.id}
                    className="bg-background-100 rounded-lg p-5 border border-background-200/70"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      {[...Array(t.rating)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-xs text-foreground-600 leading-relaxed mb-4">
                      &ldquo;{t.content}&rdquo;
                    </p>
                    <div className="flex items-center gap-3 pt-3 border-t border-background-200/70">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-9 h-9 rounded-full object-cover"
                      />
                      <div>
                        <p className="text-xs font-label font-medium text-foreground-900">
                          {t.name}
                        </p>
                        <p className="text-xs text-foreground-400">{t.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="py-12 md:py-16 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                  <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-4">
                    联系我们
                  </h2>
                  <p className="text-sm text-foreground-500 leading-relaxed mb-8">
                    有任何问题、建议或合作意向，欢迎随时联系我们。我们会在24小时内回复。
                  </p>
                  <div className="flex flex-col gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                        <i className="ri-mail-line text-primary-500"></i>
                      </div>
                      <div>
                        <p className="text-xs font-label font-medium text-foreground-900">邮箱</p>
                        <p className="text-sm text-foreground-500">hello@17cyiqi.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                        <i className="ri-map-pin-line text-accent-500"></i>
                      </div>
                      <div>
                        <p className="text-xs font-label font-medium text-foreground-900">地址</p>
                        <p className="text-sm text-foreground-500">北京市朝阳区望京科技园A座</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 flex-shrink-0">
                        <i className="ri-wechat-line text-secondary-500"></i>
                      </div>
                      <div>
                        <p className="text-xs font-label font-medium text-foreground-900">微信公众号</p>
                        <p className="text-sm text-foreground-500">17c一起</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-heading text-foreground-950 mb-4">
                    发送消息
                  </h3>
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form
                    className="bg-background-50 rounded-lg p-5 border border-background-200/70"
                  >
                    <div className="flex flex-col gap-3">
                      <div>
                        <label htmlFor="contact-name" className="block text-xs font-label text-foreground-600 mb-1">
                          姓名
                        </label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-3 py-2.5 text-sm rounded-md border border-background-200/70 bg-background-50 text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 transition-colors"
                          placeholder="请输入你的姓名"
                        />
                      </div>

                      <div>
                        <label htmlFor="contact-email" className="block text-xs font-label text-foreground-600 mb-1">
                          邮箱
                        </label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-3 py-2.5 text-sm rounded-md border border-background-200/70 bg-background-50 text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 transition-colors"
                          placeholder="请输入你的邮箱"
                        />
                      </div>

                      <div>
                        <label htmlFor="contact-subject" className="block text-xs font-label text-foreground-600 mb-1">
                          主题
                        </label>
                        <input
                          id="contact-subject"
                          name="subject"
                          type="text"
                          required
                          className="w-full px-3 py-2.5 text-sm rounded-md border border-background-200/70 bg-background-50 text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 transition-colors"
                          placeholder="请输入消息主题"
                        />
                      </div>

                      <div>
                        <label htmlFor="contact-message" className="block text-xs font-label text-foreground-600 mb-1">
                          消息
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-3 py-2.5 text-sm rounded-md border border-background-200/70 bg-background-50 text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                          placeholder="请输入你的消息（最多500字）"
                        ></textarea>
                      </div>

                      <input
                        type="text"
                        name="company_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="contact-form-spacer"
                      />

                      <button
                        type="submit"
                        disabled={formState === 'submitting'}
                        className="w-full bg-primary-500 text-background-50 py-2.5 rounded-full text-sm font-label whitespace-nowrap hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                      >
                        {formState === 'submitting' ? '发送中...' : '发送消息'}
                      </button>
                    </div>

                    {formState === 'success' && (
                      <p className="mt-3 text-sm text-center text-green-600">
                        消息已发送成功！我们会尽快回复你。
                      </p>
                    )}
                    {formState === 'error' && (
                      <p className="mt-3 text-sm text-center text-red-500">
                        {formError}
                      </p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}